import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  ShoppingCart, Trash2, Plus, Minus, ArrowRight, ShieldCheck,
  CreditCard, Lock, Tag, Receipt, ChevronRight, Wrench, Code2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useCart, cartStore } from "@/lib/cart";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/cart")({
  component: CartPage,
  head: () => ({
    meta: [
      { title: "Your Cart — CoderLity" },
      { name: "description", content: "Review the items in your cart and continue to secure checkout." },
      { property: "og:title", content: "Your Cart — CoderLity" },
      { property: "og:description", content: "Review items, update quantities, and check out securely." },
      { property: "og:url", content: "/cart" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/cart" }],
  }),
});

const PROMO_CODES: Record<string, number> = {
  WELCOME10: 0.1,
  CODERLITY20: 0.2,
};

type ManualKind = "service" | "code";

function CartPage() {
  const { items, count, subtotal } = useCart();
  const [promo, setPromo] = useState("");
  const [discount, setDiscount] = useState(0);

  const manualCard = <ManualEntryCard />;

  const tax = subtotal * 0.05;
  const discountAmount = subtotal * discount;
  const total = Math.max(0, subtotal - discountAmount + tax);

  const applyPromo = () => {
    const code = promo.trim().toUpperCase();
    if (!code) return;
    const rate = PROMO_CODES[code];
    if (!rate) {
      toast.error("Invalid promo code");
      setDiscount(0);
      return;
    }
    setDiscount(rate);
    toast.success(`Promo applied — ${rate * 100}% off`);
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="Cart"
        title="Your Cart"
        sub={count === 0 ? "Your cart is empty — add a product to get started." : `${count} item${count !== 1 ? "s" : ""} ready for checkout`}
      />

      <section className="py-12">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <nav className="mb-6 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground font-medium">Cart</span>
          </nav>

          {items.length === 0 ? (
            <EmptyCart manualForm={manualCard} />
          ) : (
            <div className="grid gap-8 lg:grid-cols-3">
              {/* Items */}
              <div className="lg:col-span-2 space-y-6">
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
                    <h2 className="text-sm font-semibold flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4 text-primary" /> Items ({count})
                    </h2>
                    <button
                      onClick={() => { cartStore.clear(); toast.success("Cart cleared"); }}
                      className="text-xs font-medium text-muted-foreground hover:text-destructive transition"
                    >
                      Clear all
                    </button>
                  </div>
                  <ul className="divide-y divide-border">
                    {items.map((it) => {
                      const kind = (it as any).meta && /script|code/i.test((it as any).meta) ? "code" : "service";
                      const isManual = it.id.startsWith("manual-");
                      const Icon = isManual && kind === "code" ? Code2 : isManual ? Wrench : Receipt;
                      return (
                        <li key={it.id} className="flex items-start gap-4 p-5">
                          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gradient-brand/10 text-primary ring-1 ring-border">
                            <Icon className="h-5 w-5" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                {it.href ? (
                                  <Link to={it.href} className="text-sm font-semibold hover:text-primary transition truncate block">
                                    {it.name}
                                  </Link>
                                ) : (
                                  <div className="text-sm font-semibold truncate">{it.name}</div>
                                )}
                                {it.meta && <div className="mt-0.5 text-xs text-muted-foreground">{it.meta}</div>}
                              </div>
                              <div className="text-sm font-bold text-gradient">${(it.price * it.qty).toFixed(2)}</div>
                            </div>
                            <div className="mt-3 flex items-center justify-between gap-3">
                              <div className="inline-flex items-center rounded-md border border-border">
                                <button
                                  onClick={() => cartStore.setQty(it.id, it.qty - 1)}
                                  className="p-1.5 hover:bg-secondary transition rounded-l-md"
                                  aria-label={`Decrease quantity of ${it.name}`}
                                >
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                                <span className="min-w-8 text-center text-sm font-medium">{it.qty}</span>
                                <button
                                  onClick={() => cartStore.setQty(it.id, it.qty + 1)}
                                  className="p-1.5 hover:bg-secondary transition rounded-r-md"
                                  aria-label={`Increase quantity of ${it.name}`}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-muted-foreground">${it.price.toFixed(2)} each</span>
                                <button
                                  onClick={() => { cartStore.remove(it.id); toast.success("Item removed"); }}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-destructive transition"
                                  aria-label={`Remove ${it.name}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {manualCard}

                <Link
                  to="/marketplace"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  ← Continue shopping
                </Link>
              </div>

              {/* Summary */}
              <aside className="lg:col-span-1">
                <div className="sticky top-24 space-y-4">
                  <div className="rounded-xl border border-border bg-card p-5">
                    <h2 className="text-sm font-semibold mb-4">Order summary</h2>

                    <div className="mb-4">
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                        <Tag className="h-3 w-3" /> Promo code
                      </label>
                      <div className="flex gap-2">
                        <Input
                          value={promo}
                          onChange={(e) => setPromo(e.target.value)}
                          placeholder="WELCOME10"
                          className="h-9"
                        />
                        <Button onClick={applyPromo} variant="outline" size="sm" className="h-9">Apply</Button>
                      </div>
                    </div>

                    <Separator className="my-3" />

                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Subtotal</dt>
                        <dd>${subtotal.toFixed(2)}</dd>
                      </div>
                      {discount > 0 && (
                        <div className="flex justify-between text-emerald-500">
                          <dt>Discount ({(discount * 100).toFixed(0)}%)</dt>
                          <dd>−${discountAmount.toFixed(2)}</dd>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">VAT (5%)</dt>
                        <dd>${tax.toFixed(2)}</dd>
                      </div>
                    </dl>

                    <Separator className="my-3" />

                    <div className="flex items-baseline justify-between">
                      <span className="text-sm font-semibold">Total</span>
                      <span className="text-2xl font-bold text-gradient">${total.toFixed(2)}</span>
                    </div>

                    <Button asChild className="mt-4 w-full bg-gradient-brand text-white shadow-glow hover:opacity-95" size="lg">
                      <Link to="/checkout">
                        <Lock className="mr-2 h-4 w-4" /> Secure checkout
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>

                    <div className="mt-4 flex flex-col gap-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> 30-day money-back guarantee</div>
                      <div className="flex items-center gap-2"><CreditCard className="h-3.5 w-3.5 text-primary" /> Card, wallet & bank transfer</div>
                      <div className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-primary" /> SSL-encrypted checkout</div>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}

function EmptyCart({ manualForm }: { manualForm?: React.ReactNode }) {
  return (
    <div className="grid gap-8 lg:grid-cols-2 items-start">
      <div className="text-center lg:text-left py-8">
        <div className="mx-auto lg:mx-0 mb-5 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-brand/10 ring-1 ring-border">
          <ShoppingCart className="h-7 w-7 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">Your cart is empty</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Discover hosting plans, software licenses, domains, and more in our marketplace.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-2">
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-brand px-5 py-2.5 text-sm font-semibold text-white shadow-glow hover:opacity-95 transition"
          >
            Browse marketplace <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/hosting"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-5 py-2.5 text-sm font-semibold hover:bg-secondary transition"
          >
            See hosting plans
          </Link>
        </div>
      </div>
      {manualForm}
    </div>
  );
}

const CODE_CATEGORIES = [
  "WordPress",
  "PHP / Laravel",
  "MERN Stack",
  "HTML / CSS",
] as const;

const SERVICE_CATEGORIES = [
  "Reviews",
  "Social Media",
  "Content Writing",
  "Design",
  "Ads Management",
  "Local Listings",
  "Accounts",
  "SEO",
  "Backlinks",
  "Tool",
  "Packages",
] as const;

function ManualEntryCard() {
  const [kind, setKind] = useState<ManualKind>("service");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState("1");

  const [serviceCategory, setServiceCategory] = useState<string>(SERVICE_CATEGORIES[0]);
  const [serviceNotes, setServiceNotes] = useState("");

  const [codeCategory, setCodeCategory] = useState<string>(CODE_CATEGORIES[0]);
  const [license, setLicense] = useState("Single site");

  const reset = () => {
    setName(""); setPrice(""); setQty("1");
    setServiceCategory(SERVICE_CATEGORIES[0]); setServiceNotes("");
    setCodeCategory(CODE_CATEGORIES[0]); setLicense("Single site");
  };

  const add = () => {
    const trimmed = name.trim();
    const p = Number(price);
    const q = Math.max(1, Math.floor(Number(qty) || 1));
    if (!trimmed) return toast.error("Enter an item name");
    if (!Number.isFinite(p) || p < 0) return toast.error("Enter a valid price");

    let meta = "";
    if (kind === "service") {
      const parts = [`${serviceCategory} • Single purchase`];
      if (serviceNotes.trim()) parts.push(serviceNotes.trim());
      meta = parts.join(" • ");
    } else {
      meta = `Script / Code • ${codeCategory} • License: ${license}`;
    }

    cartStore.add({
      id: `manual-${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: trimmed,
      price: p,
      qty: kind === "service" ? 1 : q,
      meta,
    });
    toast.success(kind === "service" ? "Service added to cart" : "Script / code added to cart");
    reset();
  };

  return (
    <div className="rounded-xl border border-dashed border-border bg-card/50 p-5">
      <div className="mb-1 flex items-center gap-2">
        <Plus className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Add a custom item</h3>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Services are single purchase. Scripts &amp; templates include a license.
      </p>

      <div className="mb-4 grid grid-cols-2 gap-2">
        <TypeOption
          active={kind === "service"}
          icon={Wrench}
          title="Service"
          desc="Single purchase"
          onClick={() => setKind("service")}
        />
        <TypeOption
          active={kind === "code"}
          icon={Code2}
          title="Plugins, Scripts & Templates"
          desc="WordPress, Laravel, MERN, HTML"
          onClick={() => setKind("code")}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Category</label>
          <select
            value={kind === "service" ? serviceCategory : codeCategory}
            onChange={(e) => kind === "service" ? setServiceCategory(e.target.value) : setCodeCategory(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
          >
            {(kind === "service" ? SERVICE_CATEGORIES : CODE_CATEGORIES).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            {kind === "service" ? "Service title" : "Script / template name"}
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={kind === "service" ? "Google review pack — 10 reviews" : "Laravel CRM starter kit"}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Unit price (USD)</label>
          <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
        </div>

        {kind === "code" ? (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Quantity / licenses</label>
            <Input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
        ) : (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Quantity</label>
            <Input value="1" disabled className="opacity-60" />
          </div>
        )}

        {kind === "service" ? (
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Notes (optional)</label>
            <Input
              value={serviceNotes}
              onChange={(e) => setServiceNotes(e.target.value)}
              placeholder="Target URL, keywords, account handle…"
            />
          </div>
        ) : (
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">License</label>
            <select
              value={license}
              onChange={(e) => setLicense(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            >
              <option>Single site</option>
              <option>5 sites</option>
              <option>Unlimited</option>
              <option>Extended / commercial</option>
            </select>
          </div>
        )}
      </div>

      <Button onClick={add} className="mt-4 w-full sm:w-auto" size="sm">
        <Plus className="mr-1.5 h-4 w-4" />
        {kind === "service" ? "Add service to cart" : "Add script to cart"}
      </Button>
    </div>
  );
}

function TypeOption({
  active, icon: Icon, title, desc, onClick,
}: {
  active: boolean;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3 text-left transition",
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary/30"
          : "border-border bg-card hover:bg-secondary/50"
      )}
    >
      <span className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
        active ? "bg-gradient-brand text-white shadow-glow" : "bg-secondary text-muted-foreground"
      )}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <div className={cn("text-sm font-semibold", active ? "text-foreground" : "text-foreground")}>{title}</div>
        <div className="text-[11px] text-muted-foreground">{desc}</div>
      </div>
    </button>
  );
}
